import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import './ClientDashboard.css';
import './BuddyPage.css';

const BuddyPage = () => {
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [invites, setInvites] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [inviteData, setInviteData] = useState({ timeSlotId: '', message: '' });

  const fetchFriends = useCallback(async () => {
    try {
      const response = await fetch('/api/buddy/friends', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFriends(data);
      }
    } catch (error) {
      console.error('Erro ao carregar amigos:', error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch('/api/buddy/friends/requests', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    }
  }, []);

  const fetchInvites = useCallback(async () => {
    try {
      const response = await fetch('/api/buddy/invites', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvites(data);
      }
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  }, []);

  const fetchTimeSlots = async () => {
    try {
      const response = await fetch(`/api/timeslots?startDate=${new Date().toISOString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeSlots(data);
      }
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFriends(), fetchRequests(), fetchInvites()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFriends, fetchRequests, fetchInvites]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await fetch(`/api/friends/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      toast.error('Erro ao pesquisar utilizadores');
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      const response = await fetch('/api/buddy/friends/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ toUserId: userId })
      });
      
      if (response.ok) {
        toast.success('Pedido de amizade enviado!');
        setSearchResults(prev => prev.filter(u => u.id !== userId));
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erro ao enviar pedido');
      }
    } catch (error) {
      toast.error('Erro ao enviar pedido de amizade');
    }
  };

  const respondToRequest = async (requestId, accept) => {
    try {
      const response = await fetch(`/api/buddy/friends/request/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accept })
      });
      
      if (response.ok) {
        toast.success(accept ? 'Pedido aceite!' : 'Pedido rejeitado');
        fetchRequests();
        if (accept) fetchFriends();
      }
    } catch (error) {
      toast.error('Erro ao responder ao pedido');
    }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm('Tem certeza que deseja remover este amigo?')) return;
    
    try {
      const response = await fetch(`/api/buddy/friends/${friendId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        toast.success('Amigo removido');
        fetchFriends();
      }
    } catch (error) {
      toast.error('Erro ao remover amigo');
    }
  };

  const openInviteModal = async (friend) => {
    setSelectedFriend(friend);
    await fetchTimeSlots();
    setShowInviteModal(true);
  };

  const sendInvite = async () => {
    if (!inviteData.timeSlotId) {
      toast.warning('Selecione um horário');
      return;
    }
    
    try {
      const response = await fetch('/api/buddy/invite', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          toUserId: selectedFriend.id,
          timeSlotId: parseInt(inviteData.timeSlotId),
          message: inviteData.message
        })
      });
      
      if (response.ok) {
        toast.success('Convite enviado!');
        setShowInviteModal(false);
        setInviteData({ timeSlotId: '', message: '' });
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erro ao enviar convite');
      }
    } catch (error) {
      toast.error('Erro ao enviar convite');
    }
  };

  const respondToInvite = async (inviteId, accept) => {
    try {
      const response = await fetch(`/api/buddy/invites/${inviteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accept })
      });
      
      if (response.ok) {
        toast.success(accept ? 'Convite aceite! Treino agendado.' : 'Convite recusado');
        fetchInvites();
      }
    } catch (error) {
      toast.error('Erro ao responder ao convite');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d 'de' MMMM, HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const renderFriends = () => (
    <div className="users-list">
      {friends.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3 className="empty-title">Ainda não tens amigos</h3>
          <p className="empty-desc">Pesquisa utilizadores para adicionar amigos e treinar juntos!</p>
        </div>
      ) : (
        friends.map(friend => (
          <div key={friend.id} className="user-card">
            <div className="user-avatar">
              {friend.avatarUrl ? (
                <img src={friend.avatarUrl} alt={friend.name} />
              ) : (
                getInitials(friend.name)
              )}
            </div>
            <div className="user-details">
              <div className="user-name">{friend.name}</div>
              <div className="user-email">{friend.email}</div>
            </div>
            <div className="user-actions">
              <button 
                className="action-btn primary"
                onClick={() => openInviteModal(friend)}
              >
                🏋️ Convidar para treinar
              </button>
              <button 
                className="action-btn outline"
                onClick={() => removeFriend(friend.id)}
              >
                ✖
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderRequests = () => (
    <div className="users-list">
      {requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📬</div>
          <h3 className="empty-title">Sem pedidos pendentes</h3>
          <p className="empty-desc">Não tens pedidos de amizade para responder</p>
        </div>
      ) : (
        requests.map(request => (
          <div key={request.id} className="request-card">
            <div className="user-avatar">
              {getInitials(request.fromUserName)}
            </div>
            <div className="request-info">
              <div className="request-name">{request.fromUserName}</div>
              <div className="request-email">{request.fromUserEmail}</div>
            </div>
            <div className="request-actions">
              <button 
                className="action-btn success"
                onClick={() => respondToRequest(request.id, true)}
              >
                ✓ Aceitar
              </button>
              <button 
                className="action-btn danger"
                onClick={() => respondToRequest(request.id, false)}
              >
                ✕ Recusar
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderInvites = () => (
    <div className="users-list">
      {invites.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏋️</div>
          <h3 className="empty-title">Sem convites de treino</h3>
          <p className="empty-desc">Quando um amigo te convidar para treinar, aparecerá aqui!</p>
        </div>
      ) : (
        invites.map(invite => (
          <div key={invite.id} className="invite-card">
            <div className="user-avatar">
              {getInitials(invite.fromUserName)}
            </div>
            <div className="invite-content">
              <div className="invite-header">
                <span className="invite-from">{invite.fromUserName}</span>
                <span className="invite-time">{formatDate(invite.createdAt)}</span>
              </div>
              <div className="invite-slot">
                <span className="invite-slot-icon">📅</span>
                <span className="invite-slot-text">
                  {invite.timeSlotDate} às {invite.timeSlotTime}
                </span>
              </div>
              {invite.message && (
                <p className="invite-message">"{invite.message}"</p>
              )}
              <div className="invite-actions">
                <button 
                  className="action-btn success"
                  onClick={() => respondToInvite(invite.id, true)}
                >
                  ✓ Aceitar convite
                </button>
                <button 
                  className="action-btn danger"
                  onClick={() => respondToInvite(invite.id, false)}
                >
                  ✕ Recusar
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderSearch = () => (
    <div>
      <div className="search-section">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Pesquisar por nome ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
          />
          <button className="search-btn" onClick={searchUsers}>
            🔍 Pesquisar
          </button>
        </div>
      </div>
      
      <div className="users-list">
        {searchResults.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3 className="empty-title">Pesquisar utilizadores</h3>
            <p className="empty-desc">Digite um nome ou email para encontrar novos amigos</p>
          </div>
        ) : (
          searchResults.map(user => (
            <div key={user.id} className="user-card">
              <div className="user-avatar">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.name} />
                ) : (
                  getInitials(user.name)
                )}
              </div>
              <div className="user-details">
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>
              <div className="user-actions">
                <button 
                  className="action-btn primary"
                  onClick={() => sendFriendRequest(user.id)}
                >
                  ➕ Adicionar amigo
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="data-card">
          <div className="empty-state">
            <div className="empty-icon">⏳</div>
            <h3 className="empty-title">A carregar...</h3>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="buddy-page">
        <div className="data-card">
          <div className="card-header">
            <h3 className="card-title">👥 Amigos de Treino</h3>
          </div>

          {/* Stats */}
          <div className="buddy-stats">
            <div className="buddy-stat">
              <div className="buddy-stat-icon">👥</div>
              <div className="buddy-stat-value">{friends.length}</div>
              <div className="buddy-stat-label">Amigos</div>
            </div>
            <div className="buddy-stat">
              <div className="buddy-stat-icon">📬</div>
              <div className="buddy-stat-value">{requests.length}</div>
              <div className="buddy-stat-label">Pedidos</div>
            </div>
            <div className="buddy-stat">
              <div className="buddy-stat-icon">🏋️</div>
              <div className="buddy-stat-value">{invites.length}</div>
              <div className="buddy-stat-label">Convites</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="buddy-tabs">
            <button 
              className={`buddy-tab ${activeTab === 'friends' ? 'active' : ''}`}
              onClick={() => setActiveTab('friends')}
            >
              👥 Amigos
            </button>
            <button 
              className={`buddy-tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              📬 Pedidos
              {requests.length > 0 && <span className="badge">{requests.length}</span>}
            </button>
            <button 
              className={`buddy-tab ${activeTab === 'invites' ? 'active' : ''}`}
              onClick={() => setActiveTab('invites')}
            >
              🏋️ Convites
              {invites.length > 0 && <span className="badge">{invites.length}</span>}
            </button>
            <button 
              className={`buddy-tab ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => setActiveTab('search')}
            >
              🔍 Adicionar
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'friends' && renderFriends()}
          {activeTab === 'requests' && renderRequests()}
          {activeTab === 'invites' && renderInvites()}
          {activeTab === 'search' && renderSearch()}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🏋️ Convidar {selectedFriend?.name}</h3>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Selecionar horário:
                </label>
                <select
                  className="search-input"
                  value={inviteData.timeSlotId}
                  onChange={(e) => setInviteData({...inviteData, timeSlotId: e.target.value})}
                  style={{ width: '100%' }}
                >
                  <option value="">-- Escolha um horário --</option>
                  {timeSlots.map(slot => (
                    <option key={slot.id} value={slot.id}>
                      {new Date(slot.startTime).toLocaleDateString('pt-PT')} · {new Date(slot.startTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}–{new Date(slot.endTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Mensagem (opcional):
                </label>
                <textarea
                  className="search-input"
                  value={inviteData.message}
                  onChange={(e) => setInviteData({...inviteData, message: e.target.value})}
                  placeholder="Ex: Vamos treinar juntos!"
                  rows={3}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="action-btn outline"
                onClick={() => setShowInviteModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="action-btn primary"
                onClick={sendInvite}
              >
                Enviar Convite
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default BuddyPage;
